/* eslint-disable import/prefer-default-export */
import { isThirdPartyUser } from "./common";
import { ESV_DETAILS } from "./Constant";

/**
 * Permission catalog rules:
 * - No roles[] → company-level (enabled per customer account in DB)
 * - roles[] present → role-level (granted when user_title matches, lowercase)
 * - "support" in roles[] is resolved for support-login sessions (GET /useraccounts/?support=true),
 *   not from user_title (support login impersonates Admin for the selected company)
 *
 * Catalog definitions live in permissionCatalogue.json and permissionRoleCatalogue.json.
 *
 * API contract (/useraccounts):
 * - Response shape: account_permissions[] with { title, permissions: [{ name, description, code }] }
 * - BE resolves company + role permissions server-side and returns the effective set only
 * - FE stores the flat result in sessionStorage (ESV_DETAILS.accountPermissions) and checks via hasPermission()
 *
 * BE resolution:
 * 1. Load company-enabled permissions (entries without roles[]) for customer_id
 * 2. Apply role_policies[user_title] — filter tab codes, apply deniedRolePermissionCodes
 * 3. Merge role permissions (entries with roles[]) where roles includes user_title (or support session)
 * 4. Return merged result as account_permissions (same API shape as today)
 *
 * hasPermission() semantics:
 * - string code → true if that code is present
 * - array of codes → OR (true if any code is present); use && for AND
 *
 * Composite helpers (company + role patterns):
 * - AND (both required): canExportSampleSubmissionData, canUploadSampleSubmissionExcel, canAccessSampleSubmissionTestDirectory, canEditSampleId, canEditSampleType, canViewUsageMetrics (show_usage_metrics + view_usage_metrics), canViewInvoice (show_invoice + view_invoice), canCreateSubmissionInvoice (show_create_submission_invoice + create_submission_invoice)
 * - Account-only: hasSampleSubmissionTestDirectoryFeature (Browse Tests / My Groups tabs on submission form)
 * - OR (either grants access): canRequestProductRetest
 * - Side-aware: canAccessTemplates (product_template vs environment_template)
 *
 * FE permission checks are advisory; BE must enforce the same rules on all APIs.
 */
export const permissions = {
  // Global
  global: {
    specialPesticides: "special_pesticides",
  },
  // Tab visibility
  tabs: {
    sampleSubmission: "view_sample_submission",
    product: "view_product",
    environment: "view_environment",
    schedulesPickups: "view_schedules_pickups",
    index: "view_index",
    documents: "view_documents",
    schedules: "view_schedules_tab",
    // qms: "view_qms",
    // workflow: "view_qms_workflow",
  },
  profile: {
    addMember: "add_member",
    deleteMember: "delete_member",
    changeMemberRole: "change_member_role",
    viewMemberStatus: "view_member_status",
    changeAdmin: "change_admin",
  },
  sampleSubmission: {
    exportData: "sample_submission_export_data",
    exportSubmissionData: "export_data",
    createProductSubmission: "sample_submission_create_product_submission",
    createEnvironmentSubmission: "sample_submission_create_environment_submission",
    uploadExcel: "sample_submission_upload_excel",
    compositeSample: "sample_submission_composite_sample",
    share: "share",
    addTest: "add_test",
    /** Account permission — Test Directory feature enabled for the customer. */
    testDirectory: "sample_submission_test_directory",
    /** Role permission for Test Directory request/unlock/group edits. */
    requestTest: "request_test",
    uploadExcelRole: "upload_excel",
  },
  onboarding: {
    addCompany: "add_company",
    onboardProduct: "onboard_product",
    onboardEnvironment: "onboard_environment",
  },
  specManager: {
    addSpec: "add_spec",
  },
  product: {
    generatePartial: "product_generate_partial",
    template: "product_template",
    analytics: "product_analytics",
    requestRetest: "product_create_retest",
    requestProductRetest: "request_product_retest",
    specManager: "view_product_spec_manager",
    receivePartial: "product_receive_partial",
    reportTableHeaderEdit: "report_table_header_edit",
    editSampleId: "product_edit_sample_id",
    editSampleIdRole: "edit_sample_id",
    editResultUnit: "product_edit_result_unit",
    editSampleType: "product_edit_sample_type",
    editSampleTypeRole: "edit_sample_type",
  },
  environment: {
    swabathon: "environment_swabathon",
    swabathonSublocation: "environment_swabathon_sublocation",
    calendar: "environment_calendar",
    analytics: "environment_analytics",
    randomizer: "environment_randomizer",
    correctiveAction: "environment_corrective_action",
    calendarPrinter: "environment_calendar_printer",
    generatePartial: "environment_generate_partial",
    specsManager: "view_environment_spec_manager",
    receivePartial: "environment_receive_partial",
    template: "environment_template",
  },
  qms: {
    workflow: "view_qms_workflow",
    dms: "qms_dms",
    ncr: "qms_ncr",
    moc: "qms_moc",
  },
  // Schedules Features
  schedules: {
    supplyRequest: "view_supply_request",
    pickupRequest: "view_pickup_request",
  },
  // My Account billing
  // Account flags (title: "Usage Metrics") replace the customer_type gate.
  usageMetrics: {
    showUsageMetrics: "show_usage_metrics",
    showInvoice: "show_invoice",
    showCreateSubmissionInvoice: "show_create_submission_invoice",
  },
  // Role flags (title: "subscriptions")
  subscriptions: {
    viewUsageMetrics: "view_usage_metrics",
    viewInvoice: "view_invoice",
    makePayment: "make_payment",
    createSubmissionInvoice: "create_submission_invoice",
  },
};

/**
 * Retrieves account permissions from esv_details in sessionStorage.
 * Permissions are fetched from API (/useraccounts/) and transformed/stored during login.
 * @returns {Object} - Flat permissions object or empty object if not found
 */
export const getAccountPermissions = () => {
  try {
    const config = sessionStorage.getItem(ESV_DETAILS);
    if (!config) return {};
    return JSON.parse(config)?.accountPermissions || {};
  } catch (error) {
    return {};
  }
};

/**
 * Checks if the user has the specified permission(s).
 * Accepts BE-resolved codes from account_permissions (company + role merged).
 * @param {string|string[]} code - A single permission code or an array of permission codes
 * @returns {boolean} - Returns true if user has the permission(s) enabled, false otherwise
 */
export const hasPermission = (code) => {
  const accountPermissions = getAccountPermissions();

  if (!accountPermissions || typeof accountPermissions !== "object") {
    return false;
  }

  if (typeof code === "string") {
    return accountPermissions[code] === true;
  }

  if (Array.isArray(code)) {
    return code.some((permissionCode) => accountPermissions[permissionCode] === true);
  }

  return false;
};

export const canUploadSampleSubmissionExcel = () => (
  hasPermission(permissions.sampleSubmission.uploadExcelRole)
  && hasPermission(permissions.sampleSubmission.uploadExcel)
);

/**
 * Third-party invitation links use Member-level access and must not add/edit tests,
 * even when account_permissions from the API includes add_test.
 */
export const canAddSampleSubmissionTest = () => (
  !isThirdPartyUser()
  && hasPermission(permissions.sampleSubmission.addTest)
);

/**
 * Third-party invitation links must not request tests or edit groups,
 * even when account_permissions includes request_test.
 */
export const canRequestTest = () => (
  !isThirdPartyUser()
  && hasPermission(permissions.sampleSubmission.requestTest)
);

/**
 * Account permission — Test Directory tabs (Browse Tests / My Groups) on the submission form.
 */
export const hasSampleSubmissionTestDirectoryFeature = () => (
  hasPermission(permissions.sampleSubmission.testDirectory)
);

/**
 * Full Test Directory page access requires both company-level and role-level permissions:
 * - sample_submission_test_directory (account, hasSampleSubmissionTestDirectoryFeature)
 * - request_test (role, via canRequestTest)
 */
export const canAccessSampleSubmissionTestDirectory = () => (
  hasSampleSubmissionTestDirectoryFeature()
  && canRequestTest()
);

/**
 * Export requires both company-level and role-level permissions:
 * - sample_submission_export_data (account)
 * - export_data (role)
 */
export const canExportSampleSubmissionData = () => (
  hasPermission(permissions.sampleSubmission.exportData)
  && hasPermission(permissions.sampleSubmission.exportSubmissionData)
);

/**
 * Request retest via account feature or support role permission (either grants access).
 */
export const canRequestProductRetest = () => (
  hasPermission([
    permissions.product.requestRetest,
  ])
);

/**
 * Sample ID Edit requires both company-level and role-level permissions:
 * - product_edit_sample_id (account)
 * - edit_sample_id (role)
 */
export const canEditSampleId = () => (
  hasPermission(permissions.product.editSampleId)
  && hasPermission(permissions.product.editSampleIdRole)
);

/**
 * Sample Type Edit requires both company-level and role-level permissions:
 * - product_edit_sample_type (account)
 * - edit_sample_type (role)
 */
export const canEditSampleType = () => (
  hasPermission(permissions.product.editSampleType)
  && hasPermission(permissions.product.editSampleTypeRole)
);

export const canShowEnvReportSpecsEntry = () => (
  hasPermission(permissions.environment.specsManager)
);

/**
 * Payment actions (Pay Now, billing details, checkout, top-up).
 * Returned under account_permissions title "subscriptions" for admin.
 */
export const canMakePayment = () => hasPermission(permissions.subscriptions.makePayment);

/**
 * Create Submission Invoice on the Invoice tab.
 * Account permission show_create_submission_invoice and role permission create_submission_invoice.
 */
export const canCreateSubmissionInvoice = () => (
  hasPermission(permissions.usageMetrics.showCreateSubmissionInvoice)
  && hasPermission(permissions.subscriptions.createSubmissionInvoice)
);

/**
 * Template access for a given side (`product` or `environment`).
 * Used by the COA preview picker (current side) and Import Template (opposite side).
 */
export const canAccessTemplates = (side) => (
  hasPermission(
    side === "environment"
      ? permissions.environment.template
      : permissions.product.template,
  )
);

/**
 * Checks whether the user has at least one tab visibility permission.
 * Used to decide if UnderMaintenance should be shown when no tabs are available.
 */
export const hasAnyTabPermission = () => {
  const accountPermissions = getAccountPermissions();

  if (!accountPermissions || typeof accountPermissions !== "object") {
    return false;
  }

  return Object.values(permissions.tabs).some(
    (permissionCode) => accountPermissions[permissionCode] === true,
  );
};

/**
 * Returns the default landing route based on available tab permissions.
 * Prefers Sample Submission when available; otherwise first permitted tab.
 */
export const getDefaultRoute = () => {
  const tabRouteMap = [
    { tab: "sampleSubmission", permission: permissions.tabs.sampleSubmission, route: "/sample-submission" },
    { tab: "product", permission: permissions.tabs.product, route: "/product/report" },
    { tab: "environment", permission: permissions.tabs.environment, route: "/environment" },
    { tab: "schedules", permission: permissions.tabs.schedules, route: "/supply-pickup" },
    { tab: "index", permission: permissions.tabs.index, route: "/index" },
    { tab: "documents", permission: permissions.tabs.documents, route: "/documents" },
  ];

  const sampleSubmissionTab = tabRouteMap.find((item) => item.tab === "sampleSubmission");
  if (sampleSubmissionTab && hasPermission(sampleSubmissionTab.permission)) {
    return sampleSubmissionTab.route;
  }

  const firstAvailableTab = tabRouteMap.find(
    ({ tab, permission }) => tab !== "sampleSubmission" && hasPermission(permission),
  );
  if (firstAvailableTab) {
    return firstAvailableTab.route;
  }

  return "/sample-submission";
};
